import { useState, useCallback } from 'react';
import { Loader2, Search } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { quoteService } from '@/services';
import { usePagination } from '@/hooks/usePagination';
import { toast } from 'sonner';

interface AssignQuotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  companyId: string;
}

const formatUSD = (value: number | null) =>
  value != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
    : '—';

export default function AssignQuotesDialog({
  open, onOpenChange, projectId, companyId,
}: AssignQuotesDialogProps) {
  const queryClient = useQueryClient();
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set());
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const pagination = usePagination({
    storageKey: 'assign_quotes_dialog',
    persistToUrl: false,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: ['unassigned-quotes', companyId, pagination.pageIndex, pagination.pageSize, pagination.debouncedSearch],
    queryFn: async () => {
      const res = await quoteService.listUnassignedPaginated(
        companyId,
        { page: pagination.pageIndex, pageSize: pagination.pageSize },
        { search: pagination.debouncedSearch || undefined }
      );
      if (res.error) throw res.error;
      return res;
    },
    enabled: open && !!companyId,
  });

  const handleAssign = useCallback(async (quoteId: string) => {
    setLoadingId(quoteId);
    const { error } = await quoteService.assignToProject(quoteId, projectId);
    setLoadingId(null);
    if (error) {
      toast.error('Failed to assign quote');
      return;
    }
    setAssignedIds(prev => new Set(prev).add(quoteId));
    toast.success('Quote assigned');
  }, [projectId]);

  const handleUnassign = useCallback(async (quoteId: string) => {
    setLoadingId(quoteId);
    const { error } = await quoteService.unassignFromProject(quoteId);
    setLoadingId(null);
    if (error) {
      toast.error('Failed to unassign quote');
      return;
    }
    setAssignedIds(prev => {
      const next = new Set(prev);
      next.delete(quoteId);
      return next;
    });
    toast.success('Quote unassigned');
  }, []);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Refresh the main project quotes grid
      queryClient.invalidateQueries({ queryKey: ['project-quotes', projectId] });
      setAssignedIds(new Set());
      pagination.resetToFirstPage();
    }
    onOpenChange(nextOpen);
  };

  const quotes = result?.data ?? [];
  const totalCount = result?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pagination.pageSize));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[850px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Assign Quotes</DialogTitle>
          <DialogDescription>
            Browse unassigned quotes and add them to this project.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search well name, API, or quote #..."
            value={pagination.searchValue}
            onChange={(e) => pagination.setSearchValue(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-muted-foreground">
            No unassigned quotes found.
          </div>
        ) : (
          <ScrollArea className="flex-1 -mx-6 px-6 max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-2 font-medium">Vendor</th>
                  <th className="pb-2 font-medium">Quote #</th>
                  <th className="pb-2 font-medium text-right">Price</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {quotes.map((q) => {
                  const qid = q.quote_id!;
                  const isAssigned = assignedIds.has(qid);
                  const isBusy = loadingId === qid;
                  return (
                    <tr key={qid} className="h-11">
                      <td className="py-2">{q.vendor?.name || '—'}</td>
                      <td className="py-2">{q.vendor_quote_number || '—'}</td>
                      <td className="py-2 text-right">{formatUSD(q.total_price)}</td>
                      <td className="py-2">
                        {q.status_name ? (
                          <Badge variant="secondary">{q.status_name}</Badge>
                        ) : '—'}
                      </td>
                      <td className="py-2 text-right">
                        {isAssigned ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isBusy}
                            onClick={() => handleUnassign(qid)}
                          >
                            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Undo'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            disabled={isBusy}
                            onClick={() => handleAssign(qid)}
                          >
                            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
        )}

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between pt-3 border-t border-border text-sm text-muted-foreground">
            <span>
              Showing {pagination.pageIndex * pagination.pageSize + 1}–
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount)} of {totalCount}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={pagination.pageIndex === 0}
                onClick={() => pagination.setPageIndex(pagination.pageIndex - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={pagination.pageIndex >= totalPages - 1}
                onClick={() => pagination.setPageIndex(pagination.pageIndex + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
